import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";

import { UserRole as ErUserRole } from "../enums";

import {
  BooleanField,
  DateTimeField,
  LongTextField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  SelectField,
  StringField,
  WysiwygField,
} from "../plugins/Cruxtify/field-types";

import {
  getAsChoices,
  InvoiceStatus,
  ManagedCaseStatus,
  ManagedCaseStatusByType,
  PaymentMethod,
  PartyType,
  SubmissionFulfillmentType,
  SubmissionStatus,
  Urgency,
} from "../enums";
import {
  BooleanProperty,
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import { ErCrudModel } from "./ErCrudModel";

/**
 * Conditional that hides admin fields whose property doesn't apply when
 * the submission is a Record Analysis case (where matching/specialty
 * routing isn't used).
 */
const hideForRecordAnalysisConditional = {
  property: "type",
  compare: "notEquals",
  value: SubmissionFulfillmentType.RecordAnalysis,
};

/**
 * The admin managed-case-status select, narrowed to the statuses the row's
 * fulfillment type allows (ManagedCaseStatusByType, the client mirror of the
 * API's ManagedCaseStatus::validForType). Offering all twelve on every row let
 * an admin pick "In Queue" for a White-Glove case, which
 * SubmissionRequestHandler::validateManagedCaseStatus then refused
 * (sentry-1542).
 *
 * Only `items` -- what the select offers -- is narrowed. `options` stays the
 * full map so a stored out-of-type value still renders its label, and a model
 * with no type (the ref instance the My Cases dynamic filter reads) still
 * offers every status to filter on.
 */
export class ManagedCaseStatusSelectField extends SelectField {
  public get items() {
    const allowed =
      ManagedCaseStatusByType[this.model?.findProperty("type")?.value];
    if (!allowed) return super.items;

    return super.items.filter((item) =>
      allowed.includes(item.value as ManagedCaseStatus)
    );
  }
}

// Properties from `requiredCaseFields` that `toAdminField` hides on their
// `_admin` variant for a Record Analysis submission. A field that needs the
// conditional on its base (non-`_admin`) definition instead attaches it
// directly, as `sub_specialty` does below.
const HIDE_FOR_RECORD_ANALYSIS_PROPERTIES = [
  "profession",
  "specialty",
  "party_type",
  "urgency",
];

/**
 * Given a field definition, returns a copy with `rules` and
 * `isPrimaryLabelField` stripped, and `id` set to `{property}_admin`.
 *
 * For properties that don't apply to Record Analysis submissions, also
 * attaches a conditional so the admin form hides them in that case.
 *
 * WHY `isPrimaryLabelField` IS STRIPPED. `requiredCaseFields` is spread into
 * `fieldDefinitions` twice -- once as-is, and once through this function -- so
 * every property in it yields TWO fields (`identifier` and `identifier_admin`).
 * `CrudModel.label` joins `getPrimaryLabelFields()`, which filters *fields*,
 * not properties, so carrying the flag onto the admin copy put each value in
 * the label twice:
 *
 *   CLAUDE-1 — CLAUDE-1 — ACDF with disc implant… — ACDF with disc implant…
 *
 * The admin variants are alternate presentations of the same properties, so
 * only the canonical field should define the label. Do not "restore" this key
 * for symmetry with the other opts -- it is load-bearing by its absence.
 */
export function toAdminField(fieldDef: { type: any; opts: any }) {
  const { rules, id, isPrimaryLabelField, ...rest } = fieldDef.opts;
  const opts: any = {
    ...rest,
    id: `${rest.property}_admin`,
  };

  if (HIDE_FOR_RECORD_ANALYSIS_PROPERTIES.includes(rest.property)) {
    opts.conditional = hideForRecordAnalysisConditional;
  }

  return {
    type: fieldDef.type,
    opts,
  };
}

export const readOnlyAfterPublish = function (submission) {
  return submission.isNew ||
    (submission as Submission).getProperty("is_editable").typedValue
    ? UserPermission.Edit
    : UserPermission.Read;
};

// The Expert Matches grid has nothing to show while a case is still being
// created, so it is hidden on the add form and restored once the submission
// exists. Same pure-function permission idiom as `readOnlyAfterPublish`.
export const hiddenUntilSaved = function (submission) {
  return submission.isNew ? UserPermission.Hidden : UserPermission.Edit;
};

/**
 * Decides Active vs. Past for a case that IS a case at all — see
 * `caseListBucket` below for the third bucket a provisional stub falls into
 * first. `archived_at` overrides status; a Fulfilled case is Past only once
 * paid, so the dashboard keeps prompting payment on an unpaid fulfilled
 * case; Expired and Cancelled are always Past. See asana-1214.
 */
export function isPastCase(submission) {
  if (submission.getProperty("archived_at").typedValue) return true;

  switch (submission.getProperty("status").typedValue) {
    case SubmissionStatus.Fulfilled:
      return !!submission.getProperty("is_paid_for").typedValue;
    case SubmissionStatus.Expired:
    case SubmissionStatus.Cancelled:
      return true;
    default:
      return false;
  }
}

/**
 * Whether the attorney has actually started managed intake — by booking the
 * consultation through the embed, or by sending a message to the medical
 * director instead. Mirrors the PHP twin,
 * `Submission::hasStartedManagedIntake()`
 * (expert-retainer/app/Models/Submission.php:981), so the pair stays
 * greppable from either side.
 */
export function hasStartedManagedIntake(submission) {
  return (
    !!submission.getProperty("appointment_booked_at").typedValue ||
    !!submission.getProperty("consultation_message").typedValue
  );
}

/**
 * A managed case row minted at method-pick time (`ensureStub` in
 * pages/managed-search.vue) before the attorney has committed to anything.
 * It has to exist for the HubSpot webhook correlation window
 * (`scopeFreshUnbookedStubFor`), but it is never something the attorney
 * should be shown, charged for, or asked to act on. See asana-1470.
 *
 * The three guards past "managed, Draft-or-Expired, no intake" are each
 * carve-outs for a real case that can reach this shape without intake ever
 * starting:
 * - `is_paid_for` — the "Already scheduled your consultation call?" self
 *   declaration pays without persisting a booking or a message.
 * - `is_invoiced` — a case manager can send an invoice on a managed Draft
 *   before the attorney finishes the wizard; a sent invoice always implies
 *   `is_invoiced` (see `Submission.php:813-815`), so this one clause covers
 *   both invoice flags.
 */
export function isProvisionalManagedStub(submission) {
  if (!submission.isManagedCase()) return false;

  const status = submission.getProperty("status").typedValue;
  if (status !== SubmissionStatus.Draft && status !== SubmissionStatus.Expired) {
    return false;
  }

  if (hasStartedManagedIntake(submission)) return false;
  if (submission.getProperty("is_paid_for").typedValue) return false;
  if (submission.getProperty("is_invoiced").typedValue) return false;

  return true;
}

/**
 * The three-way partition the client's case lists read instead of
 * `isPastCase` directly: a provisional managed stub is hidden from both
 * lists, checked first so a janitor-expired stub is caught even though
 * `isPastCase` alone would call it Past. See asana-1470.
 */
export function caseListBucket(
  submission
): "hidden" | "past" | "active" {
  if (isProvisionalManagedStub(submission)) return "hidden";
  if (isPastCase(submission)) return "past";
  return "active";
}

export class Submission extends ErCrudModel {
  public static icon = "mdi-post-outline";
  protected static _typeLabel = "Case Submission";
  protected static asProperty = "submission";
  public static api = {
    path: "submissions",
  };
  protected static routeBase = "submissions";
  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.EntityOwner]: (submission) => {
        if (!submission || submission.isNew) return UserPermission.Edit;

        if (!(submission as Submission).getProperty("archived_at").isEmpty) {
          return UserPermission.Read;
        }

        return UserPermission.Edit;
      },
      [UserRole.LoggedIn]: UserPermission.New,
    },
    UserPermission.New
  );

  protected userIdField = ["user"];

  // The submission label is `identifier` + `title` joined by CrudModel.label.
  // The mockups set an em dash with hair spaces ("Hairfield — ENT Fraud...")
  // where Cruxtify's default is a hyphen. This propagates EVERYWHERE the
  // submission label renders -- page titles, breadcrumbs, admin tables, delete
  // confirmations -- which is intended (plan 0010 Task 12).
  protected labelGlue = " — ";

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Entry Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "Entry Deleted At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "closed_at",
        label: "Entry Closed At",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "archived_at",
        label: "Entry Archived At",
        // The owning attorney may READ this -- SubmissionPolicy already ships
        // archived_at to them, and "Past cases" (Task 9) needs it. Cruxtify's
        // EntityOwner is the attorney here (see the class-level userPermissions
        // map above). `closed_at` stays Hidden: nothing in this plan reads it and
        // "past" means archived. Plan 20260810-1703-attorney-portal Task 6.
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
            [UserRole.EntityOwner]: UserPermission.Read,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "identifier",
        label: "Internal Case Identifier",
        description:
          "This is only for your reference and will never be shared.",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
            [UserRole.EntityOwner]: UserPermission.Edit,
          },
          UserPermission.New
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "title",
        label: "Case Theme",
        description:
          "Experts will see this at the top of their notification.\nEx: Delayed cancer diagnosis",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "description",
        label: "Case Description",
        maxLength: 1000,
        description:
          "Provide as much detail as necessary for experts to determine if they are a good fit for the case.\nEx: My client presented with abdominal pain, fever, and loss of appetite for several days.  They were seen in an ER and discharged home without labs or imaging.  Three days later, they were taken to another ER and diagnosed with a ruptured appendix.  Looking for an ER expert for standard of care.  This was in an academic setting.  Prefer a physician who teaches residents and/or NPs or PAs.",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "type",
        label: "Submission Type",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "party_type",
        label: "Party Type",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "urgency",
        label: "Urgency",
        description:
          'If you need an expert review/report in less than 30 days, mark your case as "Urgent".',
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "status",
        label: "Status",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      // The client-safe projection of managed_case_status (plan
      // 20260810-1703-attorney-portal Task 3/6). Holds one of ClientCaseState's
      // four values; DashboardCaseRow reads its label from ClientCaseStateLabels
      // (Task 7), never getAsChoices. The raw managed_case_status below stays
      // Hidden -- this projection is why it can.
      type: StringProperty,
      opts: {
        name: "client_case_state",
        label: "Case State",
        userPermissions: UserPermission.Read,
      },
    },
    {
      // How many experts the attorney can see on this case (Task 4/6). Read-only;
      // the API appends it, the client never writes it.
      type: NumberProperty,
      opts: {
        name: "expert_match_count",
        label: "Expert Match Count",
        userPermissions: UserPermission.Read,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "managed_case_status",
        label: "Managed Case Status",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "notes",
        label: "Case Manager Notes",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "consultation_message",
        label: "Message from Attorney",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "invoice_notes",
        label: "Invoice Notes",
        description:
          "Sent to Stripe as the invoice line description. Use this to tell the attorney the case, service, and expert referred. Leave blank to use the auto-generated description.",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "appointment_booked_at",
        label: "Appointment Booked At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        // When the consultation STARTS — distinct from appointment_booked_at,
        // which is when the booking was recorded. Populated only by the HubSpot
        // meeting webhook, so it is legitimately null in the window between a
        // successful booking and the webhook landing; the White-Glove
        // confirmation step handles that state rather than guessing a time.
        name: "appointment_starts_at",
        label: "Appointment Starts At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "state",
        label: "State",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_paid_for",
        label: "Is Paid",
        computed: true,
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_cancellable",
        label: "Is Cancellable",
        computed: true,
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_editable",
        label: "Is Editable",
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "payment_method",
        label: "Payment Method",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "stripe_invoice_id",
        label: "Stripe Invoice ID",
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_invoiced",
        label: "Is Invoiced",
        computed: true,
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_invoice_sent",
        label: "Is Invoice Sent",
        computed: true,
      },
    },
    {
      // Virtual property backing the "Invoice Status" My Cases column filter
      // only. Never persisted or rendered as a column — its value is one of the
      // InvoiceStatus states, serialized as `invoice_status` for the backend
      // Submission::filterByInvoiceStatus custom filter to translate to SQL.
      type: StringProperty,
      opts: {
        name: "invoice_status_filter",
        serializedName: "invoice_status",
        label: "Invoice Status",
        userPermissions: UserPermission.Read,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "profession",
        label: "Profession",
        relatedModel: "Profession",
        description: "Select the healthcare profession needed for this case",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "specialty",
        label: "Specialty",
        relatedModel: "Specialty",
        description:
          "Our medical directors review every case submission to ensure the right experts are notified and will contact you if they need clarification.  If you aren't quite sure what specialty you need, consider our Custom Search or our Pre-Litigation Case Analysis services.",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "sub_specialty",
        label: "Subspecialty (optional)",
        relatedModel: "SubSpecialty",
        description: "If you are unsure, leave this blank.",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          readOnlyAfterPublish
        ),
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "credit",
        label: "Credit",
        relatedModel: "Credit",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "user",
        label: "User",
        relatedModel: "User",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "manager",
        label: "Submission Manager",
        relatedModel: "User",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "expert_matches",
        label: "Expert Matches",
        relatedModel: "ExpertMatch",
        foreignProperty: "submission",
        userPermissions: UserPermission.Edit,
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "submission_events",
        label: "Submission Events",
        relatedModel: "SubmissionEvent",
        foreignProperty: "submission",
        computed: true,
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "notifications",
        label: "Notifications",
        relatedModel: "Notification",
        foreignProperty: "submission",
        computed: true,
      },
    },

    // expert
    {
      type: StringProperty,
      opts: {
        name: "cur_user_response_string",
        label: "Your Response",
        computed: true,
        sortable: false,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "cur_user_response",
        computed: true,
      },
    },
  ];

  // Fields with required rules that admins should be able to bypass
  private static requiredCaseFields = [
    {
      type: StringField,
      opts: {
        property: "identifier",
        isPrimaryLabelField: 1,
        maxLength: 30,
        cols: 12,
        sm: 6,
        md: 4,
        rules: ["required"],
      },
    },
    {
      type: StringField,
      opts: {
        property: "title",
        isPrimaryLabelField: 2,
        maxLength: 60,
        cols: 12,
        sm: 6,
        md: 4,
        rules: ["required"],
      },
    },
    {
      type: LongTextField,
      opts: { property: "description", rules: ["required"] },
    },
    {
      type: SelectField,
      opts: {
        property: "type",
        cols: 4,
        options: getAsChoices(SubmissionFulfillmentType),
        rules: ["required"],
      },
    },
    {
      type: SelectField,
      opts: {
        property: "party_type",
        cols: 4,
        options: {
          [PartyType.Plaintiff]: "Plaintiff",
          [PartyType.Defendant]: "Defendant",
        },
        rules: ["required"],
      },
    },
    {
      type: SelectField,
      opts: {
        property: "urgency",
        cols: 12,
        sm: 6,
        md: 4,
        options: {
          [Urgency.NotUrgent]: "Not Urgent",
          [Urgency.Urgent]: "Urgent",
        },
        rules: ["required"],
      },
    },
    {
      type: SelectField,
      opts: {
        property: "state",
        cols: 12,
        sm: 6,
        md: 4,
        nullLabel: "Select a State/Territory...",
        options: {
          AL: "Alabama",
          AK: "Alaska",
          AZ: "Arizona",
          AR: "Arkansas",
          CA: "California",
          CO: "Colorado",
          CT: "Connecticut",
          DE: "Delaware",
          FL: "Florida",
          GA: "Georgia",
          HI: "Hawaii",
          ID: "Idaho",
          IL: "Illinois",
          IN: "Indiana",
          IA: "Iowa",
          KS: "Kansas",
          KY: "Kentucky",
          LA: "Louisiana",
          ME: "Maine",
          MD: "Maryland",
          MA: "Massachusetts",
          MI: "Michigan",
          MN: "Minnesota",
          MS: "Mississippi",
          MO: "Missouri",
          MT: "Montana",
          NE: "Nebraska",
          NV: "Nevada",
          NH: "New Hampshire",
          NJ: "New Jersey",
          NM: "New Mexico",
          NY: "New York",
          NC: "North Carolina",
          ND: "North Dakota",
          OH: "Ohio",
          OK: "Oklahoma",
          OR: "Oregon",
          PA: "Pennsylvania",
          RI: "Rhode Island",
          SC: "South Carolina",
          SD: "South Dakota",
          TN: "Tennessee",
          TX: "Texas",
          UT: "Utah",
          VT: "Vermont",
          VA: "Virginia",
          WA: "Washington",
          WV: "West Virginia",
          WI: "Wisconsin",
          WY: "Wyoming",
          DC: "District of Columbia",
          AS: "American Samoa",
          GU: "Guam",
          MP: "Northern Mariana Islands",
          PR: "Puerto Rico",
          VI: "Virgin Islands",
          UM: "U.S. Minor Outlying Islands",
        },
        rules: ["required"],
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "profession",
        rules: ["required"],
        md: 4,
        orderBy: "order",
        orderDesc: false,
      },
    },
    {
      type: RelationshipField,
      opts: { property: "specialty", md: 4, rules: ["required"] },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    {
      type: DateTimeField,
      opts: { property: "created_at" },
    },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: DateTimeField, opts: { property: "deleted_at" } },
    { type: DateTimeField, opts: { property: "closed_at" } },
    { type: DateTimeField, opts: { property: "archived_at" } },
    ...Submission.requiredCaseFields,

    {
      type: RelationshipField,
      opts: {
        property: "sub_specialty",
        md: 4,
        formComponentName: "client-sub-specialty-field",
        conditional: hideForRecordAnalysisConditional,
      },
    },

    {
      type: RelationshipFieldMany,
      opts: {
        property: "expert_matches",
        id: "expert_matches_admin",
        cols: 12,
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          inlineEdit: true,
        },
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "expert_matches",
        cols: 12,
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          noNew: true,
          noRefresh: true,
          inlineEdit: true,
        },
        // Hidden on the add form — a new case has no matches yet.
        userPermissions: new UserPermissions({}, hiddenUntilSaved),
        // userPermissions: new UserPermissions(
        //   {
        //     [UserRole.SuperAdmin]: UserPermission.Edit,
        //   },
        //   function (submission) {
        //     const HIDDEN_STATUSES = [
        //       SubmissionStatus.Cancelled,
        //       SubmissionStatus.Expired,
        //       SubmissionStatus.Draft,
        //       SubmissionStatus.PendingPayment,
        //       SubmissionStatus.PendingReview,
        //     ];

        //     return submission.isNew ||
        //       (submission as Submission).getProperty("archived_at")
        //         .typedValue ||
        //       HIDDEN_STATUSES.includes(
        //         (submission as Submission).getProperty("status").typedValue
        //       )
        //       ? UserPermission.Hidden
        //       : UserPermission.Edit;
        //   }
        // ),
        // conditional: {
        //   property: "status",
        //   compare: "oneOf",
        //   value: [SubmissionStatus.Soliciting, SubmissionStatus.Active],
        // },
      },
    },
    // The same matches, for the managed (White-Glove / Pre-Litigation) page at
    // `pages/special-submissions/_id.vue`. A separate field rather than a reuse
    // of the one above for exactly one reason: the label.
    //
    // The shared `client` collection layout on `models/ExpertMatch.ts` says "As
    // experts respond to your submission, they will appear here" — true of a
    // Standard Search, where experts opt in, and false of a managed search,
    // where the medical director hand-picks the shortlist and no expert ever
    // "responds". Same data, same grid; asana-1220 stopped this panel
    // rendering empty at all (`_id.vue`'s `view.showShortlist` gates it on
    // `expert_match_count > 0`), so there is no empty state left to word here.
    {
      type: RelationshipFieldMany,
      opts: {
        property: "expert_matches",
        id: "expert_matches_special",
        label: "Your expert shortlist",
        cols: 12,
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
        collectionLayoutOpts: {
          noNew: true,
          noRefresh: true,
          inlineEdit: true,
        },
      },
    },
    {
      type: RelationshipField,
      opts: { property: "user" },
    },
    {
      type: RelationshipField,
      opts: { property: "manager" },
    },
    {
      type: RelationshipField,
      opts: { property: "credit" },
    },
    {
      type: SelectField,
      opts: {
        property: "payment_method",
        cols: 4,
        options: getAsChoices(PaymentMethod),
      },
    },
    {
      type: StringField,
      opts: {
        property: "stripe_invoice_id",
        cols: 4,
      },
    },
    {
      // Read-only invoice status for collection columns. Anchored on
      // is_invoiced; the chip component reads is_invoice_sent / is_paid_for
      // off the model so all three states render in one cell.
      type: BooleanField,
      opts: {
        id: "invoice_status",
        property: "is_invoiced",
        label: "Invoice",
        viewComponentName: "invoice-status-chip",
      },
    },
    {
      // The dropdown behind the "Invoice Status" My Cases dynamic filter. Its
      // four options mirror InvoiceStatusChip's states; the selected value
      // serializes as `invoice_status` (see the invoice_status_filter property)
      // for the backend custom filter. Filter-only — not a collection column.
      type: SelectField,
      opts: {
        id: "invoice_status_filter",
        property: "invoice_status_filter",
        label: "Invoice Status",
        options: getAsChoices(InvoiceStatus),
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "submission_events",
        cols: 12,
        viewComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "notifications",
        cols: 12,
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: SelectField,
      opts: {
        property: "status",
        cols: 4,
        options: getAsChoices(SubmissionStatus),
      },
    },
    {
      type: ManagedCaseStatusSelectField,
      opts: {
        id: "managed_case_status_admin",
        property: "managed_case_status",
        cols: 4,
        options: getAsChoices(ManagedCaseStatus),
        conditional: {
          property: "type",
          compare: "oneOf",
          value: [
            SubmissionFulfillmentType.WhiteGloveSearch,
            SubmissionFulfillmentType.RecordAnalysis,
          ],
        },
      },
    },
    {
      type: LongTextField,
      opts: {
        id: "consultation_message_admin",
        property: "consultation_message",
        cols: 12,
      },
    },
    {
      type: WysiwygField,
      opts: {
        id: "notes_admin",
        property: "notes",
        cols: 12,
      },
    },
    {
      type: LongTextField,
      opts: {
        id: "invoice_notes_admin",
        property: "invoice_notes",
        cols: 12,
      },
    },

    // Admin field variants (no required rules) - generated from requiredCaseFields
    ...Submission.requiredCaseFields.map(toAdminField),

    // expert
    {
      type: NumberField,
      opts: {
        property: "cur_user_response",
      },
    },
    {
      type: StringField,
      opts: {
        property: "cur_user_response_string",
        sortable: false,
      },
    },
  ];

  protected getDefaultModelLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin
      ? "admin"
      : this.$nuxt.$auth.user.user_role === ErUserRole.Expert
      ? "expert"
      : "client";
  }

  protected static modelLayoutDefinitions = [
    {
      type: "TabbedModelLayout",
      opts: {
        id: "admin",
        tabs: [
          {
            id: "overviewTab",
            label: "Overview",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "Crudform",
                headerComponents: ["send-invoice-action"],
                fieldSets: [
                  {
                    id: "0",
                    fields: [
                      "identifier_admin",
                      "title_admin",
                      "status",
                      "managed_case_status_admin",
                      "user.is_verified",
                      "type_admin",
                      "state_admin",
                      "profession_admin",
                      "specialty_admin",
                      "sub_specialty",
                      "description_admin",
                      "party_type_admin",
                      "urgency_admin",
                    ],
                  },
                  {
                    id: "notes",
                    fields: [
                      "consultation_message_admin",
                      "notes_admin",
                      "invoice_notes_admin",
                    ],
                  },
                  {
                    id: "history",
                    fields: ["submission_events"],
                  },
                  {
                    id: "meta",
                    fields: [
                      "credit",
                      "payment_method",
                      "stripe_invoice_id",
                      "user",
                      "manager",
                      "created_at",
                      "updated_at",
                      "id",
                    ],
                  },
                ],
              },
            },
          },
          {
            id: "expertsTab",
            label: "Experts",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "experts",
                fieldSets: [
                  {
                    id: "experts",
                    fields: ["expert_matches_admin"],
                  },
                ],
              },
            },
          },
          {
            id: "notifTab",
            label: "Notifications",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "notifs",
                fieldSets: [
                  {
                    id: "notif",
                    fields: ["notifications"],
                  },
                ],
              },
            },
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "expert",
        headerComponents: ["expert-match-response-model-action"],
        fieldSets: [
          {
            id: "0",
            fields: [
              "title",
              "state",
              // "specialty",
              // "sub_specialty",
              "description",
              "party_type",
              "urgency",
            ],
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "client",
        // The mono service eyebrow above the page title ("STANDARD SEARCH").
        // ModelLayout renders header components in a v-col AFTER the header
        // slot, so `assets/screens/_submission.scss` re-orders it above the
        // title rather than beside it.
        headerComponents: ["submission-type-eyebrow"],
        footerComponents: ["submission-publish-model-action"],
        disableDefaultModelActions: true,
        fieldSets: [
          {
            id: "1",
            fields: ["expert_matches"],
          },
          {
            id: "0",
            fields: [
              "identifier",
              "title",
              "state",
              "profession",
              "specialty",
              "sub_specialty",
              "description",
              "party_type",
              "urgency",
            ],
          },
        ],
      },
    },
    // The expert shortlist as rendered on `pages/special-submissions/_id.vue`,
    // and nothing else. That page hand-rolls its own status copy rather than
    // rendering a layout, so it needs a layout carrying ONLY the matches — the
    // `client-special` layout below also carries the nine case-detail fields,
    // and dropping those onto the managed page would turn a status screen into
    // a case-detail screen.
    //
    // Plan 0010 deferred portal delivery of the White-Glove shortlist as
    // backend-dependent (non-goal 3, "today delivered by email"). The API half
    // landed with the plan's own API work: `SubmissionPolicy` was hiding
    // matches on managed cases through a key that never matched
    // (`expertMatches` where the property is `expert_matches`), so the data was
    // being served all along and the exposure is now deliberate.
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "client-special-matches",
        fieldSets: [
          {
            id: "0",
            fields: ["expert_matches_special"],
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "client-special",
        maxWidth: "900px",
        footerComponents: ["submission-publish-model-action"],
        disableDefaultModelActions: true,
        fieldSets: [
          {
            id: "1",
            fields: ["expert_matches"],
          },
          {
            id: "0",
            fields: [
              "identifier",
              "title",
              "state",
              "profession",
              "specialty",
              "sub_specialty",
              "description",
              "party_type",
              "urgency",
            ],
          },
        ],
      },
    },
  ];

  protected static getDefaultCollectionLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin
      ? "admin"
      : this.$nuxt.$auth.user.user_role === ErUserRole.Expert
      ? "expert"
      : "client";
  }

  protected static collectionLayoutDefinitions = [
    {
      id: "admin",
      useModal: true,
      columnFields: [
        "created_at",
        "identifier",
        "status",
        "title",
        "user",
        "profession",
        "specialty",
        "sub_specialty",
        "type",
        "party_type",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      dynamicFilterOptions: {
        filterFields: [
          "created_at",
          "identifier",
          "description",
          "party_type",
          "profession",
          "specialty",
          "sub_specialty",
          "state",
          "status",
          "urgency",
          "title",
          "updated_at",
          "user",
          "closed_at",
          "archived_at",
          // asana-1510: without this, findFieldOption can't resolve a `type`
          // entry, so a Submission Type chip (built or restored) drops in
          // silence -- the same trap the my-cases layout's comment below
          // describes.
          "type",
        ],
        // Jeff's first comment, "Case Submissions" half (asana-995, Task 7):
        // fulfilled/expired/cancelled cases drop off by default. One notIn
        // chip now that Task 8 landed the operator, rather than three
        // notEquals chips.
        defaultFilters: [
          {
            field: "status",
            operator: "notIn",
            value: [
              SubmissionStatus.Fulfilled,
              SubmissionStatus.Expired,
              SubmissionStatus.Cancelled,
            ],
          },
        ],
      },
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      id: "my-cases",
      useModal: true,
      noNew: true,
      inlineEdit: true,
      inlineEditFields: ["managed_case_status_admin"],
      columnFields: [
        "created_at",
        "identifier",
        "managed_case_status_admin",
        "title",
        "user",
        "user.is_verified",
        // "profession",
        // "specialty",
        // "sub_specialty",
        "type",
        "invoice_status",
        // "party_type",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      dynamicFilterOptions: {
        filterFields: [
          "created_at",
          "identifier",
          "description",
          // "party_type",
          // "profession",
          // "specialty",
          // "sub_specialty",
          // "state",
          "managed_case_status_admin",
          "invoice_status_filter",
          // "urgency",
          "title",
          "updated_at",
          "user",
          "user.is_verified",
          "closed_at",
          "archived_at",
          // Without this, a `type` default resolves to nothing and the
          // round-trip drops it in silence (Gap 7) -- findFieldOption can
          // only resolve what filterFields exposes.
          "type",
        ],
        // Jeff's first + fourth comments (asana-995, Task 7): My Cases
        // hides Standard Search by default, removably -- moved out of
        // pages/my-cases.vue's mounted() (a page-mount side effect that ran
        // unconditionally on every visit, so it could never be durably
        // removed) and into the layout, where a saved set or a sticky
        // restore can win over it.
        defaultFilters: [
          {
            field: "type",
            operator: "notEquals",
            value: SubmissionFulfillmentType.Standard,
          },
        ],
      },
      staticPropertyFilters: { manager: "" },
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      id: "expert",
      useModal: true,
      noRefresh: true,
      noNew: true,
      useDynamicFields: false,
      columnFields: [
        "cur_user_response_string",
        "title",
        // "specialty",
        // "sub_specialty",
        "state",
        "urgency",
        "party_type",
      ],
      noneText:
        "There are no submissions for you to consider at this time. You'll be notified by email when a new submission is available for review.",
      emphasizeNoneText: true,
      defaultSort: [
        {
          field: "created_at",
          order: "asc",
        },
      ],
    },
    {
      id: "client",
      useModal: false,
      noRefresh: true,
      noNew: true,
      useDynamicFields: false,
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      // The client dashboard (`pages/index.vue`, plan 0010 Task 14).
      //
      // IDENTICAL to `client` above in every respect except the id, and that is
      // the entire point: `CrudModel.getCollectionLayout`
      // (`plugins/Cruxtify/CrudModel.ts:274-296`) caches layout instances in a
      // static `_collectionLayouts` and hands every caller the SAME object. So
      // `getCollectionLayout("client")` would give the dashboard the collection
      // `/submissions` is already using -- same instances, same filters, same
      // `page`, same `perPage`, same hydration state -- and the dashboard's
      // fetch (which deliberately raises `perPage` to cover the whole list)
      // would leak straight into `/submissions` and back again.
      //
      // A SEPARATE INSTANCE, not a different configuration. Nothing here should
      // diverge from `client`; if the list itself ever needs to differ, change
      // it in both or say in a comment why only one moved.
      //
      // `pages/my-cases.vue` gets away with mutating its layout in place only
      // because nothing else uses the `my-cases` id.
      id: "dashboard",
      useModal: false,
      noRefresh: true,
      noNew: true,
      useDynamicFields: false,
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      // Past cases (`pages/past-cases.vue`, plan 20260810-1703-attorney-portal
      // Task 9). A true copy of `client`/`dashboard` under its own id, for the
      // same reason `dashboard` is separate: `getCollectionLayout` caches by id
      // and hands every caller the SAME instance, so reusing `client` or
      // `dashboard` would leak this page's widened `perPage` into `/submissions`
      // and the dashboard. Keep in step with those two.
      id: "past-cases",
      useModal: false,
      noRefresh: true,
      noNew: true,
      useDynamicFields: false,
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
  ];

  public isManagedCase() {
    return [
      SubmissionFulfillmentType.WhiteGloveSearch,
      SubmissionFulfillmentType.RecordAnalysis,
    ].includes(this.getProperty("type").typedValue);
  }

  public async setToDraft() {
    return this.$nuxt.$api.PUT(`submissions/${this.id}/draft`);
  }

  public async publish() {
    return this.$nuxt.$api.PUT(`submissions/${this.id}/publish`);
  }

  public async cancel() {
    return this.$nuxt.$api.PUT(`submissions/${this.id}/cancel`);
  }

  public static async createCheckoutSession(
    productType: string,
    submissionId?: number
  ) {
    const path = submissionId
      ? `submissions/${submissionId}/purchase-credit`
      : `submissions/create-and-pay`;
    return this.$nuxt.$api.POST(path, {
      product: productType,
    });
  }

  public static applyCredit(id: number) {
    return this.$nuxt.$api.PUT(`submissions/${id}/apply-credit`);
  }

  // Returns the user's open (Draft, unpaid) managed case of this product type,
  // or null when there's nothing to resume. Lets the managed-search flow pick
  // up where the user left off (e.g. a booked-but-unpaid case after a refresh).
  public static async getOpenManagedDraft(productType: string) {
    const res = await this.$nuxt.$api.GET("submissions/open-managed-draft", {
      product_type: productType,
    });
    const data = res && (res as any).data !== undefined ? (res as any).data : res;
    return data || null;
  }

  // Creates (or reuses) the open managed Draft *before* the scheduler embed
  // renders, so a HubSpot webhook that lands ahead of the browser callback
  // always has a row to correlate against (audit 20260902-1522-hubspot-
  // boundary, Issue 2). Unlike createManagedCaseBooking, this does NOT stamp
  // appointment_booked_at or dispatch the HubSpot deal sync.
  public static async createManagedCaseStub(productType: string) {
    return this.$nuxt.$api.POST("submissions/managed-case-stub", {
      product_type: productType,
    });
  }

  public static async createManagedCaseBooking(productType: string) {
    return this.$nuxt.$api.POST("submissions/managed-case-booking", {
      product_type: productType,
    });
  }

  public static async createManagedCaseMessage(productType: string, message: string) {
    return this.$nuxt.$api.POST("submissions/managed-case-message", {
      product_type: productType,
      message,
    });
  }

  public static async createManagedCase(
    productType: string,
    paymentMethod: string,
    submissionId?: number
  ) {
    return this.$nuxt.$api.POST("submissions/managed-case", {
      product_type: productType,
      payment_method: paymentMethod,
      ...(submissionId ? { submission_id: submissionId } : {}),
    });
  }

  public async sendInvoice(
    invoiceNotes?: string,
    ccEmails?: string,
    saveCcDefault?: boolean,
    submissionIds: number[] = []
  ) {
    const body: Record<string, string | boolean | number[]> = {};
    // An empty string is a deliberate "clear the notes", so only an omitted
    // argument leaves the key off the request.
    if (invoiceNotes !== undefined) body.invoice_notes = invoiceNotes;
    if (ccEmails) body.cc_emails = ccEmails;
    // Writes the CC list back as this attorney's default. Sent even when the
    // list is empty — that combination is how the default gets cleared.
    if (saveCcDefault) body.save_cc_default = true;
    // Omitted entirely when empty, so a plain send is wire-identical to today.
    if (submissionIds.length) body.submission_ids = submissionIds;

    const res = await this.$nuxt.$api.PUT(
      `submissions/${this.id}/send-invoice`,
      body
    );
    this.set(res.data ? res.data : res, true);
    this.markAsSaved();
    return this;
  }

  public async getInvoicePreview() {
    const res = await this.$nuxt.$api.GET(
      `submissions/${this.id}/invoice-preview`
    );
    return res.data ? res.data : res;
  }

  public async getInvoiceLink() {
    const res = await this.$nuxt.$api.GET(
      `submissions/${this.id}/invoice-link`
    );
    return res.data ? res.data : res;
  }
}
